import { Switch } from "@headlessui/react";
import { Icon } from "@iconify/react";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import UpdateHooks from "../../../Hooks/UpdateHooks";

const GeneralPatient = ({ patientId }) => {
  const [account, setAccount] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [patientsData, setPatientsData] = useState(null);
  const [selectedOption, setSelectedOption] = useState("");
  const [password, setPassword] = useState("");

  const generatePassword = (event) => {
    event.preventDefault();
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let generatedPassword = "";
    for (let i = 0; i < 8; i++) {
      generatedPassword += chars[Math.floor(Math.random() * chars.length)];
    }
    setPassword(generatedPassword);
  };

  useEffect(() => {
    fetch(`https://servercareseebd.careseebd.com/api/v1/user/${patientId}`, {
      headers: {
        authorization: `Bearer ${localStorage.getItem("careSeeAccessToken")}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setPatientsData(data?.data);
        setAccount(data?.data?.isActive);
        setSelectedOption(data?.data?.accountType || "none");
      })
      .catch((err) => {
        console.error(err);
      });
  }, [patientId, refresh]);

  const handelUpdate = async (e) => {
    e.preventDefault();
    const name = e.target?.name.value;
    const email = e.target?.email.value;
    const dateOfBirth = e.target?.dateOfBirth.value;
    const phone = e.target?.phone.value;

    const payload = {
      name: name,
      phone: phone,
      dateOfBirth: dateOfBirth,
      email: email,
    };
    if (password) {
      payload.password = password;
    }

    await UpdateHooks(`https://servercareseebd.careseebd.com/api/v1/user/${patientId}`, payload);
    toast.success("Profile details updated successfully!");
    setRefresh(!refresh);
  };

  const handelUpdateAccount = async (newVal) => {
    setAccount(newVal);
    await UpdateHooks(`https://servercareseebd.careseebd.com/api/v1/user/${patientId}`, {
      isActive: newVal,
    });
    toast.success("Account status updated!");
  };

  const handelUpdateAccountType = async () => {
    await UpdateHooks(`https://servercareseebd.careseebd.com/api/v1/user/${patientId}`, {
      accountType: selectedOption ? selectedOption : "none",
    });
    toast.success("Therapy alignment updated!");
  };

  return (
    <div className="space-y-6 w-full">
      {/* 1. Account Controls (Status and Alignment) */}
      <div className="bg-slate-50/50 p-6 rounded-2xl border border-slate-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        {/* Toggle active switch */}
        <div className="flex items-center gap-3">
          <Switch
            checked={account}
            onChange={handelUpdateAccount}
            className={`${account ? "bg-secondary" : "bg-slate-200"}
              relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500/20`}
          >
            <span
              aria-hidden="true"
              className={`${account ? "translate-x-5" : "translate-x-0"}
                pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out`}
            />
          </Switch>
          <div>
            <span className="block text-sm font-bold text-slate-700">Account Status</span>
            <span className="block text-xs text-slate-400 font-medium">Toggle account access</span>
          </div>
        </div>

        {/* Therapy alignment selectors */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full md:w-auto">
          <div className="flex flex-wrap items-center gap-3 bg-white p-2 rounded-xl border border-slate-200/60">
            {["convergence", "divergence", "none"].map((opt) => (
              <label
                key={opt}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold capitalize cursor-pointer transition ${selectedOption === opt
                  ? "bg-indigo-50 text-secondary"
                  : "text-slate-550 hover:bg-slate-50"
                  }`}
              >
                <input
                  type="radio"
                  name="option"
                  value={opt}
                  checked={selectedOption === opt}
                  onChange={(e) => setSelectedOption(e.target.value)}
                  className="hidden"
                />
                <span className={`w-2 h-2 rounded-full ${selectedOption === opt ? "bg-secondary" : "bg-slate-300"}`}></span>
                <span>{opt}</span>
              </label>
            ))}
          </div>

          <button
            onClick={handelUpdateAccountType}
            className="bg-secondary hover:bg-secondary/95 text-white font-bold text-xs py-2.5 px-4 rounded-xl shadow-sm transition active:scale-95 duration-100"
          >
            Update Alignment
          </button>
        </div>
      </div>

      {/* 2. Personal Information Fields Form */}
      <form onSubmit={handelUpdate} className="space-y-4">
        <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Personal Details</h4>

        {/* Name input */}
        <div className="relative">
          <Icon icon="solar:user-linear" className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
          <input
            type="text"
            placeholder="Full Name"
            name="name"
            required
            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-secondary transition text-sm font-medium text-slate-700"
            defaultValue={patientsData?.name || ""}
          />
        </div>

        {/* Email input */}
        <div className="relative">
          <Icon icon="solar:letter-linear" className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
          <input
            type="email"
            placeholder="Email Address"
            name="email"
            required
            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-secondary transition text-sm font-medium text-slate-700"
            defaultValue={patientsData?.email || ""}
          />
        </div>

        {/* DOB input */}
        <div className="relative">
          <Icon icon="solar:calendar-linear" className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
          <input
            type="date"
            name="dateOfBirth"
            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-secondary transition text-sm font-medium text-slate-700"
            defaultValue={patientsData?.dateOfBirth || ""}
          />
        </div>

        {/* Phone input */}
        <div className="relative">
          <Icon icon="solar:phone-linear" className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
          <input
            type="tel"
            name="phone"
            placeholder="Phone Number"
            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-secondary transition text-sm font-medium text-slate-700"
            defaultValue={patientsData?.phone || ""}
          />
        </div>

        {/* Password generator */}
        <div className="relative flex items-stretch border border-slate-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-secondary transition">
          <div className="flex items-center pl-3.5 text-slate-400">
            <Icon icon="solar:lock-password-linear" className="text-lg" />
          </div>
          <input
            type="text"
            value={password}
            name="password"
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Type or generate password"
            className="flex-1 px-3 py-2.5 focus:outline-none text-sm font-medium text-slate-700"
          />
          <button
            type="button"
            onClick={generatePassword}
            className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 border-l border-slate-200 text-slate-650 font-bold text-xs transition duration-150"
          >
            Generate
          </button>
        </div>

        {/* Action Button */}
        <button
          type="submit"
          className="w-full sm:w-auto ml-auto block bg-secondary hover:bg-secondary/95 text-white font-bold py-2.5 px-6 rounded-xl shadow-md shadow-indigo-900/10 hover:shadow-lg transition duration-200 hover:-translate-y-0.5 active:translate-y-0"
        >
          Update Details
        </button>
      </form>
    </div>
  );
};

export default GeneralPatient;
